import { useContext, useEffect, useState } from 'react';
import { ReportContext, UserContext } from '../Context';
import {
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  Avatar,
  Chip,
  Tooltip,
  Skeleton,
  Grid,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import FolderOffIcon from '@mui/icons-material/FolderOff';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import axios from 'axios';
import SettingsFieldDisplay from '../tool/SettingsFieldDisplay';
import GetUserAvatar from '../tool/GetUserAvatar';
import { useIndiceInfos } from '../tool/GetIndiceInfos';

// Classe une date dans un des trois seaux temporels utilisés pour le groupement
const getBucket = (dateStr) => {
  if (!dateStr) return 'older';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return 'older';
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (date.getTime() >= startOfToday.getTime()) return 'today';
  const sevenDaysAgo = new Date(startOfToday);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
  if (date.getTime() >= sevenDaysAgo.getTime()) return 'week';
  return 'older';
};

const BUCKETS = [
  { id: 'today', label: "Aujourd'hui" },
  { id: 'week', label: 'Cette semaine' },
  { id: 'older', label: 'Plus ancien' },
];

function ReportsList({ viewType = 'personal' }) {
  const { pickReport } = useContext(ReportContext);
  const { user } = useContext(UserContext);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [reportToArchive, setReportToArchive] = useState(null);
  const [successMessage, setSuccessMessage] = useState(false);
  const [dataRetention, setDataRetention] = useState({ autoDelete: false, retentionDays: 365 });
  
  // Utilisation du hook optimisé pour les données d'indices
  const { getIndiceData, getIconComponent } = useIndiceInfos();



  useEffect(() => {
    const fetchReports = async () => {
      setLoading(true);
      try {
        const token = sessionStorage.getItem('authToken');
        let data = [];

        if (viewType === 'last50') {
          const response = await axios.get('/api/lastreports', {
            headers: { Authorization: `Bearer ${token}` },
          });
          data = response.data;
        } else if (user && (viewType === 'personal' || viewType === 'archived')) {
          const response = await axios.get(`/api/user/${user}/accessed-reports`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          
          // Filtrer selon si c'est archivé ou non
          data = response.data.filter(report => report.archived === (viewType === 'archived'));
        }

        // Rafraîchir indice/service via l'API légère et ignorer les PV supprimés
        let enrichedData = data;
        const items = data.map(({ number, year }) => ({ number, year }));
        if (items.length > 0) {
          try {
            const metadataResponse = await axios.post(
              '/api/report/metadata',
              { items },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            const metadata = Array.isArray(metadataResponse.data?.metadata)
              ? metadataResponse.data.metadata
              : [];
            if (metadataResponse.data?.dataRetention) {
              setDataRetention(metadataResponse.data.dataRetention);
            }
            if (metadata.length > 0) {
              // Ne conserver que les PV encore présents en base
              const validKeys = new Set(
                metadata
                  .filter((m) => m.found !== false)
                  .map((m) => `${m.number}-${m.year}`)
              );
              const metadataMap = new Map(
                metadata.map((m) => [`${m.number}-${m.year}`, m])
              );
              enrichedData = data
                .filter((report) => validKeys.has(`${report.number}-${report.year}`))
                .map((report) => {
                  const meta = metadataMap.get(`${report.number}-${report.year}`);
                  if (!meta || meta.found === false) return report;
                  return {
                    ...report,
                    indice: meta.indice ?? report.indice,
                    unit: meta.unit ?? report.unit,
                    restricted: meta.restricted ?? report.restricted,
                    reportType: meta.reportType ?? report.reportType,
                    createdAt: meta.createdAt ?? report.createdAt,
                    updatedAt: meta.updatedAt ?? report.updatedAt,
                  };
                });
            }
          } catch (metaError) {
            console.warn('Impossible de rafraîchir les métadonnées des PV:', metaError);
          }
        }
        
        // Classer du plus récent au plus ancien selon la date de création du PV
        if (viewType === 'personal' || viewType === 'archived') {
          enrichedData = [...enrichedData].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
        }

        setReports(enrichedData);
        setError(null);
      } catch (error) {
        console.error('Erreur lors de la récupération des rapports:', error);
        setError("Impossible de charger les rapports");
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [user, viewType]);

  const handleEdit = (report) => {
    pickReport({ number: report.number, year: report.year, new: false });
  };

  const handleOpenDialog = (report) => {
    setReportToArchive(report);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setReportToArchive(null);
  };

  const handleArchive = async () => {
    if (!reportToArchive) return;

    try {
      const token = sessionStorage.getItem('authToken');
      await axios.patch(
        `/api/user/${user}/accessed-reports/${reportToArchive.number}/${reportToArchive.year}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Retirer le rapport de la liste
      setReports((prev) =>
        prev.filter((r) => !(r.number === reportToArchive.number && r.year === reportToArchive.year))
      );
      
      // Afficher le message de succès
      setSuccessMessage(true);
    } catch (error) {
      console.error('Erreur lors de l\'archivage du procès-verbal consulté:', error);
    } finally {
      handleCloseDialog();
    }
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage(false);
  };

  // Skeleton loader pour l'état de chargement
  const renderSkeletons = () => {
    return Array(3).fill(0).map((_, index) => (
      <Card key={index} sx={{ mb: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Skeleton variant="circular" width={24} height={24} sx={{ mr: 2 }} />
            <Skeleton variant="text" width="60%" height={30} />
            <Skeleton variant="rounded" width={80} height={24} sx={{ ml: 2 }} />
          </Box>
          <Box sx={{ mt: 2 }}>
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="text" width="70%" />
          </Box>
        </CardContent>
      </Card>
    ));
  };

  // Configuration des éléments d'interface selon le type de vue
  const getEmptyStateConfig = () => {
    switch (viewType) {
      case 'archived':
        return {
          icon: <BookmarkBorderIcon sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.6, mb: 2 }} />,
          title: 'Aucun procès-verbal archivé',
          subtitle: 'Les procès-verbaux que vous archivez apparaîtront ici'
        };
      case 'last50':
        return {
          icon: <HistoryIcon sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.6, mb: 2 }} />,
          title: 'Aucun procès-verbal dans votre zone',
          subtitle: 'Les derniers procès-verbaux créés sur le système apparaîtront ici'
        };
      default: // personal
        return {
          icon: <FolderOffIcon sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.6, mb: 2 }} />,
          title: 'Aucun procès-verbal en cours',
          subtitle: 'Les procès-verbaux que vous consultez apparaîtront ici'
        };
    }
  };

  const getCardStyles = (report) => {
    const indiceData = getIndiceData(report.indice);
    return {
      borderLeft: `3px solid ${indiceData.color}`,
      background: `linear-gradient(90deg, ${indiceData.color}15 0%, rgba(255,255,255,0) 50%)`,
      '&:hover': { 
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
      }
    };
  };

  const getAvatarStyles = (report) => {
    const indiceData = getIndiceData(report.indice);
    return { 
      bgcolor: indiceData.color
    };
  };

  return (
    <Box>
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
        >
          {error}
        </Alert>
      )}
      
      {loading ? (
        renderSkeletons()
      ) : reports.length === 0 ? (
        <Card 
          sx={{ 
            textAlign: 'center', 
            p: 4, 
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}
        >
          {getEmptyStateConfig().icon}
          <Typography variant="h6" color="text.secondary">
            {getEmptyStateConfig().title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {getEmptyStateConfig().subtitle}
          </Typography>
        </Card>
      ) : (
        <Stack spacing={1}>
          {(() => {
            const getReportDate = (r) => r.createdAt;

            const groups = BUCKETS.map((bucket) => ({
              ...bucket,
              items: reports.filter((r) => getBucket(getReportDate(r)) === bucket.id),
            })).filter((g) => g.items.length > 0);

            return groups.map((group, groupIndex) => (
              <Box key={group.id} sx={{ mt: groupIndex === 0 ? 0 : 1.5 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ px: 0.5, pb: 1 }}
                >
                  <Typography
                    variant="overline"
                    sx={{
                      fontWeight: 700,
                      color: 'text.secondary',
                      letterSpacing: 1,
                      lineHeight: 1,
                    }}
                  >
                    {group.label}
                  </Typography>
                  <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: 'text.secondary',
                      opacity: 0.85,
                    }}
                  >
                    {group.items.length}
                  </Typography>
                </Stack>

                <Stack spacing={1.75}>
                  {group.items.map((report, index) => {
                    const indiceData = getIndiceData(report.indice);
                    const IconComponent = getIconComponent(indiceData.icon);

                    return (
                      <Card
                        key={`${report.number}-${report.year}-${index}`}
                        sx={{
                          borderRadius: 2,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                          transition: 'all 0.2s ease',
                          ...getCardStyles(report)
                        }}
                      >
                <CardContent sx={{ py: { xs: 1.5, sm: 1.75 }, px: { xs: 1.75, sm: 2.25 }, '&:last-child': { pb: { xs: 1.5, sm: 1.75 } } }}>
                  <Grid container alignItems="center" spacing={1.75} wrap="nowrap">
                    <Grid item>
                      <Tooltip title={indiceData.name}>
                        <Avatar sx={{ ...getAvatarStyles(report), width: 34, height: 34 }}>
                          <IconComponent sx={{ fontSize: 19 }} />
                        </Avatar>
                      </Tooltip>
                    </Grid>
                    
                    <Grid item xs zeroMinWidth>
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25 }}>
                        <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
                          <SettingsFieldDisplay fieldName="shortJudicialDistrict" />.
                          {report.indice}.
                          <SettingsFieldDisplay fieldName="judicialDivision" />.
                          {report.number}/{report.year}
                        </Typography>
                        
                        <Typography 
                          variant="body2"
                          component="span" 
                          sx={{  
                            fontWeight: 500,
                            color: 'text.secondary',
                            lineHeight: 1.3,
                          }}
                        >
                          · {indiceData.name}
                        </Typography>

                        {report.restricted && (
                          <Chip
                            icon={<LockIcon sx={{ fontSize: 14 }} />}
                            label="Restreint"
                            size="small"
                            color="warning"
                            sx={{ borderRadius: 1, fontWeight: 500, height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
                          />
                        )}
                      </Box>
                      
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        divider={<Divider orientation="vertical" flexItem sx={{ my: 0.25 }} />}
                        sx={{ mt: 0.25, color: 'text.secondary', flexWrap: 'wrap', rowGap: 0.25 }}
                      >
                        <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                          Service&nbsp;: <strong>{report.unit}</strong>
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, whiteSpace: 'nowrap' }}>
                          <Typography variant="caption" sx={{ fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center' }}>
                            Créé par&nbsp;<GetUserAvatar matricule={report.createdBy} defaultValue="..." />
                            &nbsp;le {new Date(report.createdAt).toLocaleDateString('fr-FR', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </Typography>
                        </Box>

                        {viewType !== 'last50' && (
                          <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                            {(() => {
                              const date = new Date(report.lastAccessed);
                              const datePart = date.toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                              });
                              const timePart = date.toLocaleTimeString('fr-FR', {
                                hour: '2-digit',
                                minute: '2-digit',
                              });
                              const heureWord = date.getHours() >= 2 ? 'heures' : 'heure';
                              return `Dernier accès le ${datePart} à ${timePart} ${heureWord}`;
                            })()}
                          </Typography>
                        )}
                      </Stack>

                      {dataRetention.autoDelete && report.createdAt && (() => {
                        const createdDate = new Date(report.createdAt);
                        const now = new Date();
                        const deletionDate = new Date(createdDate.getTime() + dataRetention.retentionDays * 24 * 60 * 60 * 1000);
                        const remainingMs = deletionDate.getTime() - now.getTime();
                        const remainingDays = Math.ceil(remainingMs / (24 * 60 * 60 * 1000));
                        const isUrgent = remainingDays <= 14;

                        if (remainingDays <= 0) {
                          return (
                            <Typography variant="caption" sx={{ mt: 0.25, display: 'block', color: 'error.main', fontWeight: 600, fontSize: '0.7rem' }}>
                              Suppression automatique imminente
                            </Typography>
                          );
                        }

                        return (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              mt: 0.25,
                              display: 'block', 
                              color: isUrgent ? 'error.main' : 'text.secondary',
                              fontWeight: isUrgent ? 600 : 400,
                              fontSize: '0.7rem',
                            }}
                          >
                            Suppression automatique dans {remainingDays} jour{remainingDays > 1 ? 's' : ''}
                          </Typography>
                        );
                      })()}
                    </Grid>
                    
                    <Grid item>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Modifier">
                          <IconButton
                            color="primary"
                            onClick={() => handleEdit(report)}
                            size="small"
                            sx={{
                              boxShadow: '0 2px 5px rgba(25, 118, 210, 0.2)',
                              '&:hover': { 
                                transform: 'scale(1.05)',
                                boxShadow: '0 3px 8px rgba(25, 118, 210, 0.3)'
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        {viewType === 'personal' && (
                          <Tooltip title="Archiver">
                            <IconButton
                              color="warning"
                              onClick={() => handleOpenDialog(report)}
                              size="small"
                              sx={{
                                boxShadow: '0 2px 5px rgba(212, 112, 36, 0.2)',
                                '&:hover': { 
                                  transform: 'scale(1.05)',
                                  boxShadow: '0 3px 8px rgba(212, 112, 36, 0.3)'
                                }
                              }}
                            >
                              <BookmarkIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
                    );
                  })}
                </Stack>
              </Box>
            ));
          })()}
        </Stack>
      )}

      {/* Dialogue de confirmation - uniquement pour PersonnalReports */}
      {viewType === 'personal' && (
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          aria-labelledby="archive-dialog-title"
          aria-describedby="archive-dialog-description"
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
            }
          }}
        >
          <DialogTitle id="archive-dialog-title" sx={{ pb: 1 }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              Confirmer l'archivage
            </Typography>
            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="archive-dialog-description">
              Voulez-vous vraiment archiver le procès-verbal{' '}
              <strong>{reportToArchive ? `${reportToArchive.number}/${reportToArchive.year}` : ''}</strong> ?
              <br />Il sera déplacé vers la liste des rapports archivés.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button 
              onClick={handleCloseDialog} 
              variant="outlined"
              sx={{ borderRadius: 1.5, textTransform: 'none' }}
            >
              Annuler
            </Button>
            <Button 
              onClick={handleArchive} 
              variant="contained" 
              autoFocus
              sx={{ 
                borderRadius: 1.5, 
                textTransform: 'none',
                px: 3
              }}
            >
              Archiver
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Message de succès - uniquement pour PersonnalReports */}
      {viewType === 'personal' && (
        <Snackbar
          open={successMessage}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity="success"
            variant="filled"
            sx={{ width: '100%', borderRadius: 2 }}
          >
            Procès-verbal archivé avec succès !
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
}

export default ReportsList; 